/**
 * Quick Entry NLS (Natural Language Shortcut) Parser
 * Parses natural language timesheet entries like "8h phoenix yesterday"
 * 
 * Supports:
 * - Flexible word order
 * - Bidirectional project matching with fuzzy matching
 * - Expanded hour patterns (decimals, word numbers, ranges, special phrases)
 * - Expanded date patterns (abbreviations, relative dates, date formats)
 * - Quoted descriptions and description markers
 * - Enhanced disambiguation with ranking
 */

import type { TimesheetEntry, Project } from '@/types/timesheet';
import { format } from 'date-fns';

export interface Ambiguity {
  type: 'project' | 'date' | 'hours';
  message: string;
  options?: string[];
}

export interface ParseResult {
  entry: Partial<TimesheetEntry> | null;
  confidence: 'high' | 'medium' | 'low';
  errors: string[];
  warnings: string[];
  ambiguities: Ambiguity[];
  activityType?: string;
}

export interface ProjectMatchContext {
  recentProjectIds?: string[];
  favoriteProjectIds?: string[];
}

interface ProjectMatch {
  project: Project;
  matchType: 'exact-code' | 'exact-name' | 'partial-code' | 'partial-name' | 'fuzzy';
  matchedText: string;
  score: number;
}

/**
 * Calculate Levenshtein distance between two strings for fuzzy matching
 */
function levenshteinDistance(str1: string, str2: string): number {
  const m = str1.length;
  const n = str2.length;
  
  const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(
          dp[i - 1][j],
          dp[i][j - 1],
          dp[i - 1][j - 1]
        );
      }
    }
  }
  
  return dp[m][n];
}

/**
 * Get maximum allowed edit distance based on word length
 */
function getMaxEditDistance(wordLength: number): number {
  return wordLength >= 5 ? 2 : 1;
}

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const DAY_ABBREVIATIONS: Record<string, string> = {
  'sun': 'sunday', 'mon': 'monday', 'tue': 'tuesday', 'tues': 'tuesday',
  'wed': 'wednesday', 'thu': 'thursday', 'thur': 'thursday', 'thurs': 'thursday',
  'fri': 'friday', 'sat': 'saturday',
};

const WORD_TO_NUMBER: Record<string, number> = {
  'one': 1, 'two': 2, 'three': 3, 'four': 4, 'five': 5,
  'six': 6, 'seven': 7, 'eight': 8, 'nine': 9, 'ten': 10,
  'eleven': 11, 'twelve': 12,
};

const SPECIAL_TIME_PHRASES: Record<string, number> = {
  'half day': 4,
  'half-day': 4,
  'halfday': 4,
  'full day': 8,
  'full-day': 8,
  'fullday': 8,
  'quarter day': 2,
  'quarter-day': 2,
};

const MONTH_NAMES: Record<string, number> = {
  'jan': 0, 'january': 0, 'feb': 1, 'february': 1, 'mar': 2, 'march': 2,
  'apr': 3, 'april': 3, 'may': 4, 'jun': 5, 'june': 5,
  'jul': 6, 'july': 6, 'aug': 7, 'august': 7, 'sep': 8, 'september': 8,
  'oct': 9, 'october': 9, 'nov': 10, 'november': 10, 'dec': 11, 'december': 11,
};

/**
 * Extract hours from input string
 */
function extractHours(input: string): { hours: number | null; matchedText: string } {
  let hours: number | null = null;
  let matchedText = '';

  for (const [phrase, value] of Object.entries(SPECIAL_TIME_PHRASES)) {
    if (input.includes(phrase)) {
      return { hours: value, matchedText: phrase };
    }
  }

  const hoursPatterns = [
    { regex: /(\d+(?:[.,]\d+)?)\s*-\s*(\d+(?:[.,]\d+)?)\s*(?:hours?|hrs?|h)(?:\b|$)/i, type: 'range' },
    { regex: /(\d+),(\d+)\s*(?:hours?|hrs?|h)(?:\b|$)/i, type: 'comma-decimal' },
    { regex: /(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)(?:\b|$)/i, type: 'decimal' },
    { regex: /(\d+(?:\.\d+)?)(?:hours|hrs)(?:\b|$)/i, type: 'no-space' },
    { regex: new RegExp(`\\b(${Object.keys(WORD_TO_NUMBER).join('|')})\\s*(?:hours?|hrs?|h)\\b`, 'i'), type: 'word' },
    { regex: /\b(\d+(?:\.\d+)?)\s*h(?:ours?|rs?)?(?:\b|$)/i, type: 'simple' },
  ];

  for (const { regex, type } of hoursPatterns) {
    const match = input.match(regex);
    if (match) {
      if (type === 'range') {
        const low = parseFloat(match[1].replace(',', '.'));
        const high = parseFloat(match[2].replace(',', '.'));
        hours = (low + high) / 2;
        matchedText = match[0];
      } else if (type === 'comma-decimal') {
        hours = parseFloat(`${match[1]}.${match[2]}`);
        matchedText = match[0];
      } else if (type === 'word') {
        hours = WORD_TO_NUMBER[match[1].toLowerCase()];
        matchedText = match[0];
      } else {
        hours = parseFloat(match[1].replace(',', '.'));
        matchedText = match[0];
      }
      break;
    }
  }

  return { hours, matchedText };
}

/**
 * Extract date/day from input string
 */
function extractDate(input: string, currentDate: Date): { targetDay: string | null; dayAmbiguous: boolean; matchedText: string } {
  let targetDay: string | null = null;
  let dayAmbiguous = false;
  let matchedText = '';

  if (input.includes('yesterday')) {
    const yesterday = new Date(currentDate);
    yesterday.setDate(yesterday.getDate() - 1);
    return { targetDay: DAY_NAMES[yesterday.getDay()], dayAmbiguous: false, matchedText: 'yesterday' };
  }
  
  if (input.includes('today')) {
    return { targetDay: DAY_NAMES[currentDate.getDay()], dayAmbiguous: false, matchedText: 'today' };
  }
  
  if (input.includes('tomorrow')) {
    const tomorrow = new Date(currentDate);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return { targetDay: DAY_NAMES[tomorrow.getDay()], dayAmbiguous: false, matchedText: 'tomorrow' };
  }
  
  if (input.includes('end of week') || input.includes('eow')) {
    return { targetDay: 'friday', dayAmbiguous: false, matchedText: input.includes('eow') ? 'eow' : 'end of week' };
  }
  
  if (input.includes('start of week') || input.includes('sow')) {
    return { targetDay: 'monday', dayAmbiguous: false, matchedText: input.includes('sow') ? 'sow' : 'start of week' };
  }

  const relativePatterns = [
    { regex: /\blast\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday|mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)\b/i, modifier: 'last' },
    { regex: /\bthis\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday|mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)\b/i, modifier: 'this' },
    { regex: /\bnext\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday|mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)\b/i, modifier: 'next' },
  ];

  for (const { regex } of relativePatterns) {
    const match = input.match(regex);
    if (match) {
      const dayInput = match[1].toLowerCase();
      targetDay = DAY_ABBREVIATIONS[dayInput] || dayInput;
      matchedText = match[0];
      dayAmbiguous = false;
      return { targetDay, dayAmbiguous, matchedText };
    }
  }

  for (const [abbr, fullDay] of Object.entries(DAY_ABBREVIATIONS)) {
    const abbrRegex = new RegExp(`\\b${abbr}\\b`, 'i');
    if (abbrRegex.test(input)) {
      targetDay = fullDay;
      matchedText = abbr;
      const currentDayIndex = currentDate.getDay();
      const targetDayIndex = DAY_NAMES.indexOf(fullDay);
      const adjustedCurrentIndex = currentDayIndex === 0 ? 7 : currentDayIndex;
      const adjustedTargetIndex = targetDayIndex === 0 ? 7 : targetDayIndex;
      if (adjustedTargetIndex < adjustedCurrentIndex) {
        dayAmbiguous = true;
      }
      return { targetDay, dayAmbiguous, matchedText };
    }
  }

  for (const day of DAY_NAMES) {
    if (input.includes(day)) {
      targetDay = day;
      matchedText = day;
      const currentDayIndex = currentDate.getDay();
      const targetDayIndex = DAY_NAMES.indexOf(day);
      const adjustedCurrentIndex = currentDayIndex === 0 ? 7 : currentDayIndex;
      const adjustedTargetIndex = targetDayIndex === 0 ? 7 : targetDayIndex;
      if (adjustedTargetIndex < adjustedCurrentIndex) {
        dayAmbiguous = true;
      }
      return { targetDay, dayAmbiguous, matchedText };
    }
  }

  const monthDayMatch = input.match(/\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+(\d{1,2})(?:st|nd|rd|th)?\b/i);
  if (monthDayMatch) {
    const month = MONTH_NAMES[monthDayMatch[1].toLowerCase()];
    const day = parseInt(monthDayMatch[2], 10);
    const targetDate = new Date(currentDate.getFullYear(), month, day);
    return { targetDay: DAY_NAMES[targetDate.getDay()], dayAmbiguous: false, matchedText: monthDayMatch[0] };
  }

  const ordinalMatch = input.match(/\b(\d{1,2})(?:st|nd|rd|th)\b/i);
  if (ordinalMatch) {
    const day = parseInt(ordinalMatch[1], 10);
    const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return { targetDay: DAY_NAMES[targetDate.getDay()], dayAmbiguous: false, matchedText: ordinalMatch[0] };
  }

  const slashDateMatch = input.match(/\b(\d{1,2})\/(\d{1,2})\b/);
  if (slashDateMatch) {
    const month = parseInt(slashDateMatch[1], 10) - 1;
    const day = parseInt(slashDateMatch[2], 10);
    const targetDate = new Date(currentDate.getFullYear(), month, day);
    return { targetDay: DAY_NAMES[targetDate.getDay()], dayAmbiguous: false, matchedText: slashDateMatch[0] };
  }

  const isoDateMatch = input.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (isoDateMatch) {
    const targetDate = new Date(
      parseInt(isoDateMatch[1], 10),
      parseInt(isoDateMatch[2], 10) - 1,
      parseInt(isoDateMatch[3], 10)
    );
    return { targetDay: DAY_NAMES[targetDate.getDay()], dayAmbiguous: false, matchedText: isoDateMatch[0] };
  }

  return { targetDay: DAY_NAMES[currentDate.getDay()], dayAmbiguous: false, matchedText: '' };
}

/**
 * Find matching projects using bidirectional and fuzzy matching
 */
function findProjectMatches(input: string, projects: Project[]): ProjectMatch[] {
  const projectMatches: ProjectMatch[] = [];
  const inputWords = input.split(/\s+/).filter(w => w.length >= 3);

  let projectMarkerWord = '';
  const projectMarkerMatch = input.match(/\b(?:on|for|to)\s+(\S+)/i);
  if (projectMarkerMatch) {
    projectMarkerWord = projectMarkerMatch[1].toLowerCase();
  }

  for (const project of projects) {
    const projectName = project.name.toLowerCase();
    const projectCode = project.code.toLowerCase();

    if (input.includes(projectCode)) {
      projectMatches.push({
        project,
        matchType: 'exact-code',
        matchedText: projectCode,
        score: 100,
      });
      continue;
    }

    if (input.includes(projectName)) {
      projectMatches.push({
        project,
        matchType: 'exact-name',
        matchedText: projectName,
        score: 90,
      });
      continue;
    }

    let matched = false;
    for (const word of inputWords) {
      if (word.length < 3) continue;

      if (projectName.includes(word)) {
        projectMatches.push({
          project,
          matchType: 'partial-name',
          matchedText: word,
          score: 70,
        });
        matched = true;
        break;
      }

      const projectNameWords = projectName.split(/\s+/);
      for (const pWord of projectNameWords) {
        if (pWord.length >= 3 && word.includes(pWord)) {
          projectMatches.push({
            project,
            matchType: 'partial-name',
            matchedText: pWord,
            score: 65,
          });
          matched = true;
          break;
        }
      }
      if (matched) break;

      if (projectCode.includes(word)) {
        projectMatches.push({
          project,
          matchType: 'partial-code',
          matchedText: word,
          score: 80,
        });
        matched = true;
        break;
      }
    }

    if (!matched && projectMarkerWord && projectMarkerWord.length >= 3) {
      if (projectName.includes(projectMarkerWord) || projectCode.includes(projectMarkerWord)) {
        projectMatches.push({
          project,
          matchType: 'partial-name',
          matchedText: projectMarkerWord,
          score: 75,
        });
      }
    }
  }

  if (projectMatches.length === 0) {
    for (const word of inputWords) {
      if (word.length < 3) continue;
      const maxDistance = getMaxEditDistance(word.length);

      for (const project of projects) {
        const projectName = project.name.toLowerCase();
        const projectCode = project.code.toLowerCase();

        const codeDistance = levenshteinDistance(word, projectCode);
        if (codeDistance <= maxDistance && codeDistance > 0) {
          projectMatches.push({
            project,
            matchType: 'fuzzy',
            matchedText: word,
            score: 50 - codeDistance * 10,
          });
          continue;
        }

        const nameWords = projectName.split(/\s+/);
        for (const nameWord of nameWords) {
          if (nameWord.length < 3) continue;
          const nameDistance = levenshteinDistance(word, nameWord);
          if (nameDistance <= maxDistance && nameDistance > 0) {
            projectMatches.push({
              project,
              matchType: 'fuzzy',
              matchedText: word,
              score: 40 - nameDistance * 10,
            });
            break;
          }
        }
      }
    }
  }

  const uniqueMatches = new Map<string, ProjectMatch>();
  for (const match of projectMatches) {
    const existing = uniqueMatches.get(match.project.id);
    if (!existing || match.score > existing.score) {
      uniqueMatches.set(match.project.id, match);
    }
  }

  return Array.from(uniqueMatches.values()).sort((a, b) => b.score - a.score);
}

/**
 * Extract description from input
 */
function extractDescription(
  input: string,
  quotedDescription: string,
  hoursMatchedText: string,
  dateMatchedText: string,
  selectedProject: Project | null,
  projectMatchedText: string
): string {
  if (quotedDescription) {
    return quotedDescription;
  }

  const descriptionMarkers = [
    /\bdoing\s+(.+?)(?:\s+(?:on|for|to|yesterday|today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday|mon|tue|wed|thu|fri|sat|sun|last|this|next|\d+(?:[.,]\d+)?\s*h)|\s*$)/i,
    /\bworking\s+on\s+(.+?)(?:\s+(?:on|for|to|yesterday|today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday|mon|tue|wed|thu|fri|sat|sun|last|this|next|\d+(?:[.,]\d+)?\s*h)|\s*$)/i,
  ];

  for (const pattern of descriptionMarkers) {
    const match = input.match(pattern);
    if (match) {
      return match[1].trim();
    }
  }

  let remainingInput = input;

  if (hoursMatchedText) {
    remainingInput = remainingInput.replace(new RegExp(hoursMatchedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), ' ');
  }

  if (dateMatchedText) {
    remainingInput = remainingInput.replace(new RegExp(dateMatchedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), ' ');
  }

  if (selectedProject) {
    remainingInput = remainingInput.replace(new RegExp(selectedProject.code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), ' ');
    remainingInput = remainingInput.replace(new RegExp(selectedProject.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), ' ');
    if (projectMatchedText) {
      remainingInput = remainingInput.replace(new RegExp(projectMatchedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), ' ');
    }
  }

  remainingInput = remainingInput.replace(/\b(on|for|to|doing|working)\b/gi, ' ');

  const description = remainingInput.replace(/\s+/g, ' ').trim();

  if (!description || description.length < 3) {
    return 'Time entry';
  }

  return description.charAt(0).toUpperCase() + description.slice(1);
}

/**
 * Parse quick entry with validation, confidence levels, and ambiguity detection
 * Enhanced with flexible word order, fuzzy matching, and expanded patterns
 */
export function parseQuickEntryWithValidation(
  input: string,
  projects: Project[],
  currentDate: Date = new Date(),
  _context?: ProjectMatchContext
): ParseResult {
  const result: ParseResult = {
    entry: null,
    confidence: 'low',
    errors: [],
    warnings: [],
    ambiguities: [],
  };

  const lowerInput = input.trim().toLowerCase();

  if (!lowerInput) {
    result.errors.push('Please enter a time entry');
    return result;
  }

  let quotedDescription = '';
  let inputWithoutQuotes = lowerInput;
  const quotedMatch = lowerInput.match(/"([^"]+)"|'([^']+)'/);
  if (quotedMatch) {
    quotedDescription = (quotedMatch[1] || quotedMatch[2]).trim();
    inputWithoutQuotes = lowerInput.replace(quotedMatch[0], ' ').trim();
  }

  const { hours, matchedText: hoursMatchedText } = extractHours(inputWithoutQuotes);
  const { targetDay, dayAmbiguous, matchedText: dateMatchedText } = extractDate(inputWithoutQuotes, currentDate);
  const sortedMatches = findProjectMatches(inputWithoutQuotes, projects);

  let selectedProject: Project | null = null;
  let projectMatchedText = '';

  if (sortedMatches.length === 0) {
    result.errors.push('No project found in your input');
    result.confidence = 'low';
  } else if (sortedMatches.length === 1) {
    selectedProject = sortedMatches[0].project;
    projectMatchedText = sortedMatches[0].matchedText;
    if (sortedMatches[0].matchType === 'fuzzy') {
      result.warnings.push(`Matched "${sortedMatches[0].matchedText}" to project "${selectedProject.name}" (fuzzy match)`);
    }
  } else {
    const topMatch = sortedMatches[0];
    const secondMatch = sortedMatches[1];

    if (topMatch.score - secondMatch.score >= 20) {
      selectedProject = topMatch.project;
      projectMatchedText = topMatch.matchedText;
      if (topMatch.matchType === 'fuzzy') {
        result.warnings.push(`Matched "${topMatch.matchedText}" to project "${selectedProject.name}" (fuzzy match)`);
      }
    } else {
      result.ambiguities.push({
        type: 'project',
        message: 'Multiple projects match your input:',
        options: sortedMatches.slice(0, 5).map(m => `${m.project.name} (${m.project.code})`),
      });
      selectedProject = topMatch.project;
      projectMatchedText = topMatch.matchedText;
      result.confidence = 'medium';
    }
  }

  const description = extractDescription(
    inputWithoutQuotes,
    quotedDescription,
    hoursMatchedText,
    dateMatchedText,
    selectedProject,
    projectMatchedText
  );

  if (dayAmbiguous && targetDay) {
    const dayIndex = DAY_NAMES.indexOf(targetDay);
    const currentDayIndex = currentDate.getDay();
    const diff = dayIndex - currentDayIndex;
    
    const lastWeekDate = new Date(currentDate);
    lastWeekDate.setDate(lastWeekDate.getDate() + diff - 7);

    const thisWeekDate = new Date(currentDate);
    thisWeekDate.setDate(thisWeekDate.getDate() + diff);

    result.ambiguities.push({
      type: 'date',
      message: `Which ${targetDay.charAt(0).toUpperCase() + targetDay.slice(1)}?`,
      options: [
        `Last ${targetDay.charAt(0).toUpperCase() + targetDay.slice(1)} (${format(lastWeekDate, 'dd MMM')})`,
        `This ${targetDay.charAt(0).toUpperCase() + targetDay.slice(1)} (${format(thisWeekDate, 'dd MMM')})`,
      ],
    });
    if (result.confidence !== 'low') {
      result.confidence = 'medium';
    }
  }

  if (!hours) {
    result.errors.push('No hours specified');
    result.confidence = 'low';
  } else if (hours > 24) {
    result.errors.push('Hours cannot exceed 24 in a single day');
    result.confidence = 'low';
  } else if (hours > 12) {
    result.warnings.push(`${hours} hours seems high for one day. Did you mean ${hours / 10}?`);
    if (result.confidence !== 'low') {
      result.confidence = 'medium';
    }
  } else if (hours < 0.5) {
    result.warnings.push('Less than 0.5 hours - is this correct?');
  }

  if (targetDay === 'saturday' || targetDay === 'sunday') {
    result.warnings.push('Weekend work detected - ensure this is approved');
  }

  if (hours && selectedProject && targetDay) {
    result.entry = {
      projectId: selectedProject.id,
      costCentreId: selectedProject.costCentres[0]?.id || '',
      description: description,
      hours: {
        monday: targetDay === 'monday' ? hours : 0,
        tuesday: targetDay === 'tuesday' ? hours : 0,
        wednesday: targetDay === 'wednesday' ? hours : 0,
        thursday: targetDay === 'thursday' ? hours : 0,
        friday: targetDay === 'friday' ? hours : 0,
        saturday: targetDay === 'saturday' ? hours : 0,
        sunday: targetDay === 'sunday' ? hours : 0,
      },
    };

    if (result.errors.length === 0 && result.ambiguities.length === 0 && result.warnings.length === 0) {
      result.confidence = 'high';
    } else if (result.errors.length === 0 && result.ambiguities.length > 0) {
      result.confidence = 'medium';
    } else if (result.errors.length > 0) {
      result.confidence = 'low';
      result.entry = null;
    }
  } else {
    if (!selectedProject && !result.errors.includes('No project found in your input')) {
      result.errors.push('Project not found');
    }
    result.confidence = 'low';
  }

  return result;
}
